import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import { point } from '@turf/helpers';
import maplibre from '../../../lib/maplibre';
import MapContext from '../MapContext';
import { toLngLat } from '../../../lib/map_helpers';

const interpolate = (from, to, progress) => [
  from[0] + (to[0] - from[0]) * progress,
  from[1] + (to[1] - from[1]) * progress
];

export default class Marker extends React.Component {
  constructor(props) {
    super(props);

    this.tooltipNode = document.createElement('div');
    this.lngLat = toLngLat(props.position);
    this.mapItem = {
      type: props.type,
      toFeatures: () => [point(this.lngLat)],
      getLngLat: () => this.lngLat,
      displayAt: (lngLat) => this.displayAt(lngLat)
    };
    this.showTooltip = this.showTooltip.bind(this);
    this.hideTooltip = this.hideTooltip.bind(this);
  }

  componentDidMount() {
    const { map, register } = this.context;

    this.iconElement = document.createElement('div');
    const element = document.createElement('div');
    element.appendChild(this.iconElement);
    element.addEventListener('mouseenter', this.showTooltip);
    element.addEventListener('mouseleave', this.hideTooltip);

    this.popup = new maplibre.Popup({
      anchor: 'bottom',
      className: 'map-tooltip',
      closeButton: false,
      closeOnClick: false,
      maxWidth: 'none',
      offset: [0, 0]
    }).setDOMContent(this.tooltipNode);
    this.marker = new maplibre.Marker({ element, anchor: this.iconAnchor(), rotationAlignment: 'map' });
    this.marker.setPopup(this.popup);
    this.applyIcon();
    this.applyAppearance();
    this.marker.setLngLat(this.lngLat).addTo(map);

    register(this.mapItem);
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.icon, this.props.icon)) this.applyIcon();
    this.applyAppearance();

    const lngLat = toLngLat(this.props.position);
    if (!isEqual(lngLat, this.lngLat)) {
      this.lngLat = lngLat;
      if (!this.displacedTo) this.moveTo(lngLat);
    }
  }

  componentWillUnmount() {
    const { unregister, isRemoved } = this.context;

    cancelAnimationFrame(this.slideFrame);
    unregister(this.mapItem);
    if (isRemoved()) return;

    this.marker.remove();
  }

  applyIcon() {
    const { html, url, size, className } = this.props.icon;
    const [width, height] = size;

    this.iconElement.className = className || '';
    this.iconElement.style.width = `${width}px`;
    this.iconElement.style.height = `${height}px`;
    this.iconElement.innerHTML = url ? `<img src="${url}" width="${width}" height="${height}" alt="">` : html;
    this.popup.setOffset([0, this.iconAnchor() === 'center' ? -height / 2 : -height]);
  }

  iconAnchor() {
    return this.props.icon.anchor || 'bottom';
  }

  applyAppearance() {
    this.marker.setRotation(this.props.rotation);
    this.marker.setOpacity(String(this.props.opacity));
    this.marker.getElement().style.zIndex = this.props.zIndexOffset;
  }

  displayAt(lngLat) {
    this.displacedTo = lngLat;
    this.moveTo(lngLat || this.lngLat, { animate: false });
  }

  moveTo(lngLat, { animate = true } = {}) {
    cancelAnimationFrame(this.slideFrame);

    if (!animate || !this.props.slideDuration) {
      this.marker.setLngLat(lngLat);
      return;
    }

    const from = this.marker.getLngLat().toArray();
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / this.props.slideDuration, 1);
      this.marker.setLngLat(interpolate(from, lngLat, progress));
      if (progress < 1) this.slideFrame = requestAnimationFrame(step);
    };
    this.slideFrame = requestAnimationFrame(step);
  }

  showTooltip() {
    if (!this.props.children || this.popup.isOpen()) return;

    this.popup.setLngLat(this.marker.getLngLat()).addTo(this.context.map);
  }

  hideTooltip() {
    this.popup.remove();
  }

  render() {
    return ReactDOM.createPortal(this.props.children || null, this.tooltipNode);
  }
}

Marker.contextType = MapContext;

Marker.defaultProps = {
  opacity: 1,
  rotation: 0,
  slideDuration: 0,
  zIndexOffset: 0
};

Marker.propTypes = {
  position: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({ lat: PropTypes.number, lng: PropTypes.number })
  ]).isRequired,
  icon: PropTypes.shape({
    html: PropTypes.string,
    url: PropTypes.string,
    size: PropTypes.arrayOf(PropTypes.number).isRequired,
    anchor: PropTypes.oneOf(['bottom', 'center']),
    className: PropTypes.string
  }).isRequired,
  type: PropTypes.string,
  opacity: PropTypes.number,
  rotation: PropTypes.number,
  slideDuration: PropTypes.number,
  zIndexOffset: PropTypes.number,
  children: PropTypes.node
};
